/**
 * Post-sign-in return path (`/signin?redirect=<path>`).
 *
 * The platform-dashboard pages on this same host (/status, /docs, /tools, ...)
 * link here to sign a visitor in and expect to get them back. Two rules:
 *
 *   1. Open-redirect guard — ONLY same-origin relative paths are honoured:
 *      must start with exactly one `/`; `//host`, `scheme:`, backslashes
 *      (browsers normalise `\` to `/`) and control characters are rejected.
 *   2. Paths the SPA router does not own must be a real browser load
 *      (`location.assign`) so the request reaches the ingress →
 *      platform-dashboard rather than the SPA's 404 route.
 *
 * Pure module (no imports) so it is unit-testable under `node --test`.
 */

// Path prefixes served by platform-dashboard / the API gateway, not this SPA.
export const NON_SPA_PREFIXES = [
  '/status', '/docs', '/tools', '/bringup', '/write', '/edge', '/preflight', '/api'
]

/** Returns the safe path, or null when `raw` must not be followed. */
export function sanitizeRedirect(raw) {
  if (typeof raw !== 'string') return null
  const path = raw.trim()
  if (!path || path.length > 2048) return null
  if (path[0] !== '/' || path[1] === '/') return null
  if (path.includes('\\')) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(path)) return null
  // Belt-and-braces: resolved against a dummy origin it must stay on it.
  try {
    const base = 'https://spa.invalid'
    if (new URL(path, base).origin !== base) return null
  } catch {
    return null
  }
  // Never bounce back into the sign-in route itself (loop).
  const pathname = path.split(/[?#]/)[0]
  if (pathname === '/signin' || pathname === '/login') return null
  return path
}

/** True when `path` belongs to platform-dashboard / the API, not the SPA. */
export function isNonSpaPath(path) {
  const pathname = String(path).split(/[?#]/)[0]
  return NON_SPA_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

/**
 * Navigate to a (raw, untrusted) return path. Returns 'location' | 'router'
 * | null (rejected). Callers must have stored the token — and therefore the
 * session cookie — BEFORE calling this, so a full load is already signed in.
 */
export function navigateAfterLogin(raw, { router, location = globalThis.location } = {}) {
  const path = sanitizeRedirect(raw)
  if (!path) return null
  if (isNonSpaPath(path)) {
    location.assign(path)
    return 'location'
  }
  if (router && router.currentRoute?.value?.fullPath !== path) {
    Promise.resolve(router.push(path)).catch(() => {})
  }
  return 'router'
}
